import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { MaskedField } from '@sysmobil/synoro-packages';
import phoneMaskArray from './phoneMaskArray.json';
import { identify } from 'js/library/utils/API/auth/identify';
import { requestCode } from 'js/library/utils/API/auth/requestCode';
import { useAuth } from 'js/contexts/AuthContext';
import { usePartnerUserForms } from 'js/contexts/PartnerUserFormsContext';
import cfac22 from 'js/library/utils/cfac22';

const keyOf = (field) => field?.fieldName || field?.name || field?.key || field?.id;
const phoneType = (field) => ['cellPhone', 'phone', 'telephone'].includes(field?.fieldType);
const removeMask = (value, field) =>
  ['cpf', 'cnpj', 'cep', 'rg'].includes(field?.fieldType) ? value.replace(/\D/g, '') : value.trim();
const phoneIsValid = (value) => /^\+\d{10,15}$/.test(value.replace(/\s/g, ''));

export default function DynamicIdentifierInput() {
  const navigate = useNavigate();
  const { t } = useTranslation('app');
  const { setIdentifyAuth, setRequestCodeAuth } = useAuth();
  const { indexKey, partnerId } = usePartnerUserForms();
  const isPhone = phoneType(indexKey);
  const [country, setCountry] = useState(() => {
    const c = phoneMaskArray[0];
    return [c.ddd, c.mask, c.fullMask];
  });
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const mask = isPhone ? country[1] : indexKey?.inputMask || '';
  const label = indexKey?.label || indexKey?.placeholder || keyOf(indexKey) || 'Identificador';
  const identifierValue = useMemo(
    () => (isPhone ? `${country[0]}${inputValue.replace(/\D/g, '')}` : inputValue),
    [country, inputValue, isPhone]
  );
  const submit = async (event) => {
    event.preventDefault();
    const identifier = isPhone
      ? `+${removeMask(identifierValue, indexKey).replace(/^\+/, '')}`
      : removeMask(identifierValue, indexKey);
    if (!identifier || (isPhone && !phoneIsValid(identifier))) {
      setError(isPhone ? 'Número de celular inválido.' : 'Informe um valor válido.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resolvedPartnerId = partnerId || cfac22('partnerId');
      const identified = await identify({ identifier, partnerId: resolvedPartnerId });
      if (!identified?.success)
        throw new Error(identified?.error || 'Não foi possível identificar o usuário.');
      const found = identified.data?.found ?? identified.data?.permissions?.found ?? false;
      setIdentifyAuth({ identifier, data: identified.data });
      if (!found) {
        setRequestCodeAuth({ cellPhone: null, id: null, newUser: true });
        navigate('/cadastroUsuario');
        return;
      }
      const requested = await requestCode({
        identifier,
        ...(isPhone ? { cellPhone: identifier } : {}),
        partnerId: resolvedPartnerId,
        newUser: false,
      });
      if (requested?.error) throw new Error(requested.error);
      setRequestCodeAuth({
        cellPhone: isPhone ? identifier : null,
        id: requested.id,
        newUser: false,
      });
      navigate('/inserirCodigo');
    } catch (exception) {
      setError(exception?.error || exception?.message || 'Não foi possível continuar.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box
      component="form"
      onSubmit={submit}
      sx={{
        minHeight: '80vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        p: 2,
      }}
    >
      <Typography variant="h6" sx={{ textAlign: 'center' }}>
        {isPhone ? t('general.tell_your_phone_number') : `Informe seu ${label.toLowerCase()}`}
      </Typography>
      <Box sx={{ width: '100%', maxWidth: 450, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {isPhone ? (
          <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
            <Select
              value={JSON.stringify(country)}
              onChange={(event) => {
                setCountry(JSON.parse(event.target.value));
                setInputValue('');
              }}
              sx={{
                width: 112,
                minWidth: 112,
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 24,
                  px: 1.5,
                  whiteSpace: 'nowrap',
                },
              }}
              aria-label={t('general.country')}
            >
              {phoneMaskArray.map((item) => (
                <MenuItem
                  key={item.id}
                  value={JSON.stringify([item.ddd, item.mask, item.fullMask])}
                >
                  {item.emoji} {item.initials} {item.ddd}
                </MenuItem>
              ))}
            </Select>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MaskedField
                mask={inputValue ? mask : undefined}
                value={inputValue}
                placeholder={t('general.phone_number')}
                onChange={(event) => setInputValue(event.target.value)}
                autoFocus
                fullWidth
              />
            </Box>
          </Box>
        ) : (
          <TextField
            fullWidth
            autoFocus
            label={label}
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            error={Boolean(error)}
            inputProps={{ inputMode: indexKey?.inputType === 'email' ? 'email' : 'text' }}
            InputProps={mask ? { inputComponent: MaskedField, inputProps: { mask } } : undefined}
          />
        )}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </Box>
      <Button
        type="submit"
        variant="contained"
        disabled={loading || !inputValue.replace(/\D/g, '')}
        sx={{ width: '100%', maxWidth: 450, height: 42, borderRadius: '4px' }}
      >
        {loading ? <CircularProgress size={22} color="inherit" /> : t('general.login')}
      </Button>
    </Box>
  );
}
